import { isPlatformBrowser } from '@angular/common';
import {
  HttpClient,
  HttpContext,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';

import {
  EMPTY,
  NEVER,
  Observable,
  Subject,
  fromEvent,
  map,
  merge,
  tap,
} from 'rxjs';

import { environment } from '@env/environment';

import {
  AuthMethod,
  AuthServiceError,
  AuthToken,
} from '@personal-area/global/authorization/models';
import { AuthService } from '@personal-area/global/authorization/services';
import { IS_PRIVATE_API, IS_API_GATEWAY } from '@personal-area/global/http/models';
import {
  AppleJwtToken,
  AppleSignInEventEnum,
  ClientErrors,
  ConnectSocialForm,
  ExtendedSocialUser,
  SocialNetwork,
  SocialNetworksEnum,
  SocialUser,
  socialNetworksURLs,
} from '@personal-area/global/social-authorization/models';
import { ConnectedSocialNetwork } from '@personal-area/global/users/models';

import { AutoRegistrationService } from './auto-registration.service';
import { decodeBase64 } from './base64';
import { FacebookLoginProvider } from './facebook-login-provider';
import { GoogleLoginProvider } from './google-login-provider';
import { SocialAuthService } from './social-auth.service';

const availableSocialNetworks = ['google', 'facebook'];

declare let AppleID: any;

@Injectable({ providedIn: 'root' })
export class SocialNetworksService {
  private readonly _platformId = inject(PLATFORM_ID);
  private readonly _onLoad: Subject<ExtendedSocialUser>;
  private _onError: Subject<any | HttpErrorResponse>;

  constructor(
    private _http: HttpClient,
    private _authService: AuthService,
    private _socialAuthService: SocialAuthService,
    private _autoRegService: AutoRegistrationService,
  ) {
    if (isPlatformBrowser(this._platformId)) {
      AppleID = window['AppleID'] || {};
    }

    this._onLoad = new Subject<ExtendedSocialUser>();
    this._onError = new Subject<HttpErrorResponse>();

    merge(this._authService.onLogin, this._authService.onLogout).subscribe(() =>
      this._autoRegService.reset(),
    );
  }

  public checkAuthCookie(headers: HttpHeaders): Observable<AuthToken> {
    if (!this._autoRegService.checkSocial()) {
      return EMPTY;
    }

    const data = this._autoRegService.getSocialData();

    if (!data) {
      this._autoRegService.reset();
      return EMPTY;
    }

    this._onLoad.next(data);
    return this._login(
      data.provider as SocialNetworksEnum,
      data.authToken,
      headers,
    ).pipe(
      tap({
        error: ({ error }) => {
          if (error?.exception?.code !== AuthServiceError.CaptchaChallenge) {
            this._autoRegService.reset();
          }
        },
      }),
    );
  }

  get onLoad(): Observable<ExtendedSocialUser> {
    return this._onLoad.asObservable();
  }

  public login(platform: SocialNetworksEnum): void {
    const params = new URLSearchParams(window.location.search);

    let callbackQuery = '',
      callback = window.location.pathname;
    if (params.has('callback')) {
      [callback, callbackQuery] = params.get('callback')?.split('?');

      new URLSearchParams(callbackQuery).forEach((val, key) =>
        params.set(key, val),
      );

      params.delete('callback');
    }

    params.set('utm_nooverride', '1');

    this._autoRegService.socialAuthCallback =
      callback + '?' + params.toString();

    params.delete('utm_nooverride');

    window.location.href = `${
      window.location.origin
    }/auth/${platform}/login?${params.toString()}`;
  }

  /**
   * Get user social profile.
   * @param {string} platform Social platform type
   * @returns {Observable<SocialUser>} Observable object for subscription
   */
  public auth(
    platform: SocialNetworksEnum,
    userId: number,
  ): Observable<SocialUser> {
    let provider: string;
    let requestData: unknown;
    let social: Observable<ExtendedSocialUser> = NEVER;

    let isPrivateEmail: boolean;

    switch (platform) {
      case SocialNetworksEnum.Facebook:
        provider = FacebookLoginProvider.PROVIDER_ID;
        break;
      case SocialNetworksEnum.Google:
        provider = GoogleLoginProvider.PROVIDER_ID;
        break;
      case SocialNetworksEnum.Apple:
        requestData = {
          clientId: environment.social.apple.clientId,
          scope: 'email name',
          redirectURI: `https://${document.location.host}/cabinet/login`,
          state: environment.apiData.stateString,
          nonce: userId,
          usePopup: true,
        };
        break;
      default:
        break;
    }

    if (provider) {
      social = this._socialAuthService.signIn(provider).pipe(
        map((userData) => {
          if (platform === SocialNetworksEnum.Facebook) {
            userData.id = userData.response?.token_for_business || userData.id;
          }
          return userData;
        }),
      );
    }

    if (requestData) {
      AppleID.auth.init(requestData);
      AppleID.auth.signIn(requestData);
      const success = fromEvent(document, AppleSignInEventEnum.Success);
      const error = fromEvent(document, AppleSignInEventEnum.Error);

      social = merge(success, error).pipe(
        map((res: any) => {
          if (res.type === AppleSignInEventEnum.Success) {
            const parsedToken = decodeBase64<AppleJwtToken>(
              res.detail.authorization.id_token.split('.')[1],
            );
            isPrivateEmail = parsedToken?.is_private_email === 'true';

            if (res.detail.user) {
              return {
                authToken: res.detail.authorization.id_token,
                provider: SocialNetworksEnum.Apple.toUpperCase(),
                id: parsedToken && parsedToken.sub,
                email: res.detail.user.email,
                name: `${res.detail.user.name.firstName} ${res.detail.user.name.lastName}`,
                isPrivateEmail,
              } as ExtendedSocialUser;
            } else {
              const result = {
                authToken: res.detail.authorization.id_token,
                provider: SocialNetworksEnum.Apple.toUpperCase(),
                id: parsedToken && parsedToken.sub,
                isPrivateEmail,
              };

              if (parsedToken && parsedToken.email) {
                result['email'] = parsedToken.email;
              }

              return result as ExtendedSocialUser;
            }
          } else {
            throw new Error(res.detail.error);
          }
        }),
      );
    }

    return social.pipe(
      tap(
        (user: SocialUser) => {
          this._onLoad.next(user);
        },
        (err: HttpErrorResponse) => {
          if (err.error) {
            this._onError.next(
              this._parseError(err, ClientErrors.SocialAuthCancelledErrorCode),
            );
          }
        },
      ),
    );
  }

  /**
   * Fetch social network list
   * @return {Observable<SocialNetwork[]>}
   */
  public fetch(socials: ConnectedSocialNetwork[]): Observable<SocialNetwork[]> {
    return this._http
      .get<{ items: SocialNetwork[] }>(socialNetworksURLs.fetch, {
        context: new HttpContext().set(IS_API_GATEWAY, true),
      })
      .pipe(
        map((resp: { items: SocialNetwork[] }) => {
          resp.items = resp.items.filter(
            (item) =>
              item.isEnabled &&
              availableSocialNetworks.some((key) => key === item.name),
          );
          resp.items.forEach((item) => {
            if (socials.find((s) => s.socialType === item.name)) {
              item.isConnected = true;
            }
          });

          return resp.items;
        }),
      );
  }

  public connect(userId: number, data: ConnectSocialForm): Observable<void> {
    return this._http.post<void>(socialNetworksURLs.connect(userId), data, {
      context: new HttpContext()
        .set(IS_PRIVATE_API, true)
        .set(IS_API_GATEWAY, true),
    });
  }

  public disconnect(userId: number, platform: string): Observable<void> {
    return this._http.delete<void>(
      socialNetworksURLs.disconnect(userId, platform),
      {
        context: new HttpContext()
          .set(IS_PRIVATE_API, true)
          .set(IS_API_GATEWAY, true),
      },
    );
  }

  private _login(
    platform: SocialNetworksEnum,
    token: string,
    headers: HttpHeaders,
  ): Observable<AuthToken> {
    return this._authService.login(
      {
        email: platform,
        password: token,
      },
      false,
      AuthMethod.Social,
      headers,
    );
  }

  private _parseError(
    err: HttpErrorResponse,
    code?: string,
  ): HttpErrorResponse | { error: { exception: { token: string } } } {
    const parsedError = err.error
      ? err
      : {
          error: {
            exception: {
              token: `"${err}"`,
            },
          },
        };
    if (code && parsedError.error) {
      parsedError.error.exception = {
        ...parsedError.error.exception,
        code,
      };
    }
    return parsedError;
  }
}
